/**
 * Page Header - the internal-app page heading in the F design language.
 * Accent dash + uppercase eyebrow (optionally numbered like the public
 * SectionHeading), a proper h1 title with display-weight tracking, capped
 * subtitle width, styled breadcrumbs, and slots for badge / actions / an
 * "Updated ·" stamp. Backward compatible with the original props.
 */

import React from 'react';
import { Box, Typography, Breadcrumbs, Link, Chip } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode, DISPLAY, eyebrowSx } from '../../context/ThemeContext';
import UpdatedStamp from './UpdatedStamp';

function PageHeader({ title, subtitle, breadcrumbs, actions, badge, eyebrow, number, stamp }) {
  const navigate = useNavigate();
  const { palette } = useThemeMode();

  const eyebrowText = eyebrow ? (number ? `${number} / ${eyebrow}` : eyebrow) : null;

  return (
    <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<Icon icon="ph:caret-right" width={12} height={12} color={palette.textMuted} />}
          sx={{ mb: 1, '& .MuiBreadcrumbs-separator': { mx: 0.6 } }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return isLast ? (
              <Typography key={crumb.label} sx={{ fontSize: '0.78rem', fontWeight: 600, color: palette.textSecondary }}>
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={crumb.label}
                component="button"
                underline="hover"
                onClick={() => navigate(crumb.path)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  color: palette.textMuted,
                  '&:hover': { color: palette.text },
                  '&:focus-visible': { outline: `2px solid ${palette.accent}`, outlineOffset: 2, borderRadius: '4px' },
                }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      {/* Eyebrow with accent dash (the F kicker) */}
      {eyebrowText && (
        <Typography
          sx={{
            ...eyebrowSx,
            color: palette.accent,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.75,
            '&::before': { content: '""', width: 22, height: '1.5px', bgcolor: palette.accent, display: 'inline-block' },
          }}
        >
          {eyebrowText}
        </Typography>
      )}

      {/* Title and Actions Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1.5,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 800,
                letterSpacing: '-0.028em',
                lineHeight: 1.15,
                fontSize: { xs: '1.45rem', md: '1.7rem' },
                color: palette.text,
              }}
            >
              {title}
            </Typography>
            {badge && (
              <Chip
                label={badge}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  borderRadius: 999,
                  backgroundColor: palette.accentBg,
                  color: palette.accent,
                  letterSpacing: '0.03em',
                }}
              />
            )}
          </Box>
          {subtitle && (
            <Typography sx={{ mt: 0.4, fontSize: '0.88rem', color: palette.textSecondary, maxWidth: 520, lineHeight: 1.55 }}>
              {subtitle}
            </Typography>
          )}
          {stamp && <UpdatedStamp date={stamp} sx={{ mt: 0.6 }} />}
        </Box>

        {actions && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PageHeader;
